import { createSlice } from "@reduxjs/toolkit"

import {
    actionButtonsAdapter,
    attachmentsAdapter,
    collectionsAdapter,
    conceptsAdapter,
    itemsAdapter,
    workspacesAdapter,
} from "./adapters"

import {
    fetchItem,
    prefixId,
    ResourcePrefix,
    fetchAttachment,
    fetchCollection,
    fetchConcept,
    fetchWorkspace,
    fetchActionButton,
} from "./thunks"
import { IResourceCatalog } from "./index"

export const resourcesSlice = createSlice({
    name: "resources",

    initialState: {
        loadingResources: [],
        actionButtons: actionButtonsAdapter.getInitialState(),
        attachments: attachmentsAdapter.getInitialState(),
        collections: collectionsAdapter.getInitialState(),
        concepts: conceptsAdapter.getInitialState(),
        items: itemsAdapter.getInitialState(),
        workspaces: workspacesAdapter.getInitialState(),
    } as IResourceCatalog,

    reducers: {},

    extraReducers: (builder) => {
        builder
            .addCase(fetchActionButton.pending, (state, action) => {
                // to make sure we don't fetch the same resource multiple times
                const id = action.meta.arg
                state.loadingResources.push(
                    prefixId(ResourcePrefix.ActionButton, id),
                )
            })
            .addCase(fetchActionButton.fulfilled, (state, action) => {
                const actionButton = action.payload

                actionButtonsAdapter.addOne(state.actionButtons, actionButton)

                // to indicate that we have finished fetching it
                const index = state.loadingResources.indexOf(
                    prefixId(ResourcePrefix.ActionButton, actionButton.id),
                )
                state.loadingResources.splice(index, 1)
            })

        builder
            .addCase(fetchAttachment.pending, (state, action) => {
                // to make sure we don't fetch the same resource multiple times
                const id = action.meta.arg
                state.loadingResources.push(
                    prefixId(ResourcePrefix.Attachment, id),
                )
            })
            .addCase(fetchAttachment.fulfilled, (state, action) => {
                const attachment = action.payload

                attachmentsAdapter.addOne(state.attachments, attachment)

                // to indicate that we have finished fetching it
                const index = state.loadingResources.indexOf(
                    prefixId(ResourcePrefix.Attachment, attachment.id),
                )
                state.loadingResources.splice(index, 1)
            })

        builder
            .addCase(fetchCollection.pending, (state, action) => {
                // to make sure we don't fetch the same resource multiple times
                const id = action.meta.arg
                state.loadingResources.push(
                    prefixId(ResourcePrefix.Collection, id),
                )
            })
            .addCase(fetchCollection.fulfilled, (state, action) => {
                const collection = action.payload

                collectionsAdapter.addOne(state.collections, collection)
                // we only register the rest, if the item exists
                if (collection.doesExist && collection.workspace?.doesExist) {
                    workspacesAdapter.addOne(
                        state.workspaces,
                        collection.workspace,
                    )
                }

                // to indicate that we have finished fetching it
                const index = state.loadingResources.indexOf(
                    prefixId(ResourcePrefix.Collection, collection.id),
                )
                state.loadingResources.splice(index, 1)
            })

        builder
            .addCase(fetchConcept.pending, (state, action) => {
                // to make sure we don't fetch the same resource multiple times
                const id = action.meta.arg
                state.loadingResources.push(
                    prefixId(ResourcePrefix.Concept, id),
                )
            })
            .addCase(fetchConcept.fulfilled, (state, action) => {
                const concept = action.payload

                conceptsAdapter.addOne(state.concepts, concept)
                // we only register the rest, if the item exists
                if (concept.doesExist && concept.workspace?.doesExist) {
                    workspacesAdapter.addOne(
                        state.workspaces,
                        concept.workspace,
                    )
                }

                // to indicate that we have finished fetching it
                const index = state.loadingResources.indexOf(
                    prefixId(ResourcePrefix.Concept, concept.id),
                )
                state.loadingResources.splice(index, 1)
            })

        builder
            .addCase(fetchItem.pending, (state, action) => {
                // to make sure we don't fetch the same resource multiple times
                const id = action.meta.arg
                state.loadingResources.push(prefixId(ResourcePrefix.Item, id))
            })
            .addCase(fetchItem.fulfilled, (state, action) => {
                const item = action.payload

                itemsAdapter.addOne(state.items, item)
                // we only register the rest, if the item exists
                if (item.doesExist && item.concept?.doesExist) {
                    conceptsAdapter.addOne(state.concepts, item.concept)

                    if (item.concept.workspace?.doesExist) {
                        workspacesAdapter.addOne(
                            state.workspaces,
                            item.concept.workspace,
                        )
                    }
                }

                // to indicate that we have finished fetching it
                const index = state.loadingResources.indexOf(
                    prefixId(ResourcePrefix.Item, item.id),
                )
                state.loadingResources.splice(index, 1)
            })

        builder
            .addCase(fetchWorkspace.pending, (state, action) => {
                // to make sure we don't fetch the same resource multiple times
                const id = action.meta.arg
                state.loadingResources.push(
                    prefixId(ResourcePrefix.Workspace, id),
                )
            })
            .addCase(fetchWorkspace.fulfilled, (state, action) => {
                const workspace = action.payload

                workspacesAdapter.addOne(state.workspaces, workspace)

                // to indicate that we have finished fetching it
                const index = state.loadingResources.indexOf(
                    prefixId(ResourcePrefix.Workspace, workspace.id),
                )
                state.loadingResources.splice(index, 1)
            })
    },
})

export default resourcesSlice.reducer
